// piano-frontend/src/App.js - SettingsPage import 및 라우트 추가
import { useState, useEffect, useRef, useMemo, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { koKR } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/ko';
import { buildPianoTheme } from './theme/pianoTheme';
import api from './api/axios';
import { isServerDownError, reportSuspectedOutage, useServerDown } from './api/serverStatus';
import { MaintenanceScreen, MaintenanceDialog } from './components/ServerMaintenance';
import LoginPage from './pages/LoginPage';
import NoticeListPage from './pages/NoticeListPage';
import NoticeDetailPage from './pages/NoticeDetailPage';
import NoticeCreatePage from './pages/NoticeCreatePage';
import NoticeEditPage from "./pages/NoticeEditPage";
import PracticePage from './pages/PracticePage';
import PracticeDetailPage from './pages/PracticeDetailPage';
import LessonPage from "./pages/LessonPage";
import LessonRequestManagementPage from "./pages/LessonRequestManagementPage";
import AdminPage from "./pages/AdminPage";
import MyInfoPage from "./pages/MyInfoPage";
import SettingsPage from "./pages/SettingsPage";
import FeatureGuidePage from "./pages/FeatureGuidePage";
import GuideCoachmark from "./components/GuideCoachmark";
import BottomNavBar from './components/BottomNavBar';
import TopHeader from './components/TopHeader';
import ActivityPage from './pages/ActivityPage';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import MySchedulePage from './pages/MySchedulePage';
import { DialogProvider } from './contexts/DialogProvider';
import { MetronomeProvider } from './metronome/MetronomeProvider';
import MetronomeSheet from './metronome/MetronomeSheet';
import MetronomeMiniBar from './metronome/MetronomeMiniBar';

// public/manifest.json 의 theme_color 와 반드시 같은 값을 유지할 것 — 설치형 PWA의
// 상태바 배경이 이 값으로 칠해지므로, 아이콘 명암 기준을 여기에 맞춰야 한다.
const MANIFEST_THEME_COLOR = '#121212';

// 다크모드 Context
export const DarkModeContext = createContext({
  darkMode: false,
  toggleDarkMode: () => {},
});

export const useDarkMode = () => useContext(DarkModeContext);

function AppContent({ user, setUser, loading, setLoading }) {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionCheckedRef = useRef(false);
  const serverDown = useServerDown();

  // 관리 페이지 keep-alive: 한 번 방문하면 언마운트하지 않고 숨겨서
  // 운영 설정 등 편집 중이던 상태를 다른 메뉴에 다녀와도 유지한다.
  const onAdmin = location.pathname === "/admin";
  const [adminMounted, setAdminMounted] = useState(false);

  useEffect(() => {
    if (!user) {
      setAdminMounted(false);
      return;
    }
    if (user.role === "ADMIN" && location.pathname === "/admin") {
      setAdminMounted(true);
    }
  }, [user, location.pathname]);

  useEffect(() => {
    if (sessionCheckedRef.current) return;
    if (location.pathname === '/login') {
      setLoading(false);
      return;
    }

    sessionCheckedRef.current = true;
    
    const checkSession = async () => {
      try {
        const res = await api.get('/auth/check');
        setUser(res.data);
      } catch (err) {
        // 서버 재시작 등으로 응답이 없으면 로그인으로 보내지 않고 점검 화면을 띄운다
        if (isServerDownError(err) && await reportSuspectedOutage()) return;
        setUser(null);
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [location.pathname, navigate, setUser, setLoading]);

  // 세션 확인·로그인 단계에서 서버가 죽어 있으면 앱 전체를 점검 화면으로 대체
  if (serverDown && !user) {
    return <MaintenanceScreen />;
  }

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "100px" }}>로그인 확인 중...</div>;
  }

  const handleLogout = () => {
    setUser(null);
    sessionCheckedRef.current = false;
  };

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={setUser} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    // 메트로놈은 화면을 옮겨도 계속 울려야 하므로 라우트 바깥(셸)에 둔다
    <MetronomeProvider user={user}>
    <Box
      sx={{
        minHeight: '100vh',
        // 고정 헤더/하단바가 안전영역만큼 두꺼워지므로 콘텐츠 인셋도 함께 키운다
        pt: 'calc(56px + env(safe-area-inset-top, 0px))',
        pb: 'calc(56px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <TopHeader user={user} onLogout={handleLogout} />
      {/* 회원에게 계정당 1회 사용자 메뉴(기능 안내) 위치를 알려준다 */}
      {user.role === "MEMBER" && <GuideCoachmark />}
      <Routes>
        <Route path="/notice" element={<NoticeListPage user={user} />} />
        <Route path="/notice/new" element={<NoticeCreatePage />} />
        <Route path="/notice/:id" element={<NoticeDetailPage user={user} />} />
        <Route path="/notice/:id/edit" element={<NoticeEditPage />} />
        <Route path="/practice" element={<PracticePage user={user} />} />
        <Route path="/practice/:date" element={<PracticeDetailPage user={user} />} />
        <Route path="/lesson" element={<LessonPage user={user} setUser={setUser} />} />
        <Route
          path="/lesson-requests"
          element={(user.role === "ADMIN" || user.role === "TEACHER") ? <LessonRequestManagementPage user={user} /> : <Navigate to="/notice" />}
        />
        <Route 
          path="/my-schedule" 
          element={user?.role === "MEMBER" ? <MySchedulePage user={user} /> : <Navigate to="/notice" />} 
        />
        <Route path="/myinfo" element={<MyInfoPage user={user} />} />
        <Route path="/settings" element={<SettingsPage user={user} />} />
        <Route path="/guide" element={<FeatureGuidePage user={user} />} />
        {/* 실제 렌더링은 아래 keep-alive 박스에서 담당하고, 라우트는 권한 가드만 한다 */}
        <Route
          path="/admin"
          element={user.role === "ADMIN" ? <></> : <Navigate to="/notice" />}
        />
        <Route
          path="/activities"
          element={(user?.role === "ADMIN" || user?.role === "TEACHER") ? <ActivityPage /> : <Navigate to="/notice" />}
        />
        <Route path="*" element={<Navigate to="/notice" />} />
      </Routes>
      {user.role === "ADMIN" && (adminMounted || onAdmin) && (
        <Box sx={{ display: onAdmin ? "block" : "none" }}>
          <AdminPage active={onAdmin} />
        </Box>
      )}
      {user.role === "MEMBER" && <MetronomeMiniBar />}
      <BottomNavBar userRole={user.role} />
      {user.role === "MEMBER" && <MetronomeSheet />}
      {/* 사용 중 서버가 죽으면 현재 화면을 보존한 채 점검 모달로 덮는다 */}
      {serverDown && <MaintenanceDialog />}
    </Box>
    </MetronomeProvider>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('darkMode', String(newValue));
      return newValue;
    });
  };

  // body에 다크모드 클래스 추가 (CSS 스타일링용)
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // 다크모드에 따라 status bar theme-color 동적 변경
  //
  // 주의: 설치형 PWA(WebAPK)에서 크롬은 상태바 "배경"을 manifest.json 의 theme_color 로
  // 고정해 칠하면서, 상태바 "아이콘 명암"은 이 meta 값으로 판단한다. 그래서 meta 만
  // 모드별로 바꾸면 배경(항상 #121212)과 아이콘(라이트 모드에선 어두운색)이 정반대로
  // 갈려 시계·배터리가 안 보인다. 헤더가 상태바 영역을 직접 칠하는 엣지-투-엣지가
  // 실제로 켜진 경우(안전영역 인셋 > 0)에만 모드별 색을 쓰고, 그 외 설치형에서는
  // manifest 와 같은 값으로 고정해 배경과 아이콘 기준을 일치시킨다.
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) return;

    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-top,0px);pointer-events:none;visibility:hidden';
    document.body.appendChild(probe);
    const safeAreaTop = probe.offsetHeight;
    probe.remove();

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    const paintedByManifest = isStandalone && safeAreaTop === 0;

    metaThemeColor.setAttribute(
      'content',
      paintedByManifest ? MANIFEST_THEME_COLOR : (darkMode ? '#1e1e1e' : '#FAFAF7')
    );
  }, [darkMode]);

  const theme = useMemo(() => buildPianoTheme(darkMode), [darkMode]);

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="ko"
          localeText={koKR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
          <DialogProvider>
            <BrowserRouter>
              <AppContent user={user} setUser={setUser} loading={loading} setLoading={setLoading} />
              <PWAInstallPrompt />
            </BrowserRouter>
          </DialogProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </DarkModeContext.Provider>
  );
}